class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    /* ==============================
       PERFORMANCE / SEARCH STATE
    ============================== */

    this.cachedResults = new Map();
    this.maxCacheSize = 20;

    this.searchController = null;
    this.searchRequestId = 0;
    this.searchTimer = null;

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');

    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');

    this.isOpen = false;
    this.statusElement = null;
    this.loadingText = null;

    this.quickSearchTrending = this.querySelectorAll(
      '.quickSearchTrending .link'
    );

    this.setupEventListeners();

    this.quickSearchTrending.forEach((element) => {
      element.addEventListener(
        'click',
        this.quickSearchTrendingClick.bind(this)
      );
    });
  }


  /* ==============================
     EVENT LISTENERS
  ============================== */

  setupEventListeners() {
    const form = this.querySelector('form.search');

    if (form) {
      form.addEventListener(
        'submit',
        this.onFormSubmit.bind(this)
      );
    }

    if (!this.input) return;

    /*
      250ms debounce:
      Faster than the old 500ms while still preventing
      a request for every single keystroke.
    */
    this.input.addEventListener('input', () => {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        this.onChange();
      }, 250);
    });

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );

    document.addEventListener(
      'click',
      this.onDocClick.bind(this)
    );

    if (this.closeStickySearchButton) {
      this.closeStickySearchButton.addEventListener(
        'click',
        this.onCloseStickySearchClick.bind(this)
      );
    }
  }


  /* ==============================
     QUERY
  ============================== */

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }


  /* ==============================
     INPUT CHANGE
  ============================== */

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.cancelSearch();
      this.close(true);
      this.showTrendingAndProducts();
      return;
    }

    this.getSearchResults(searchTerm);
  }


  /* ==============================
     FORM SUBMIT
  ============================== */

  onFormSubmit(event) {
    const query = this.getQuery();

    if (!query.length) {
      event.preventDefault();
      return;
    }

    const selected = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selected) {
      event.preventDefault();
      selected.click();
    }
  }


  /* ==============================
     FOCUS
  ============================== */

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showTrendingAndProducts();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }


  /* ==============================
     FOCUS OUT
  ============================== */

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 0);
  }


  /* ==============================
     KEYBOARD
  ============================== */

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }


  onKeyup(event) {
    /*
      If search is empty, restore trending products.
    */
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }


  /* ==============================
     KEYBOARD NAVIGATION
  ============================== */

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    /*
      IMPORTANT:
      Your custom predictive-search Liquid uses:

      <div class="cps__row" role="option">

      NOT <li>.

      The old JS searched only for <li>, which was incorrect.
    */

    const allElements = Array.from(
      this.querySelectorAll('[role="option"]')
    ).filter((element) => {
      const link = element.querySelector('a, button');
      return link && !element.closest('.cps__footer');
    });

    if (!allElements.length) return;

    const selectedElement = this.querySelector(
      '[role="option"][aria-selected="true"]'
    );

    const moveUp = direction === 'up';

    let activeElement;

    if (!selectedElement) {
      /*
        Arrow Down starts at first result.
        Arrow Up starts at last result.
      */
      activeElement = moveUp
        ? allElements[allElements.length - 1]
        : allElements[0];
    } else {
      const currentIndex = allElements.indexOf(selectedElement);

      if (currentIndex === -1) {
        activeElement = allElements[0];
      } else if (moveUp) {
        activeElement =
          allElements[currentIndex - 1] ||
          allElements[allElements.length - 1];
      } else {
        activeElement =
          allElements[currentIndex + 1] ||
          allElements[0];
      }
    }

    if (!activeElement) return;

    /*
      Clear previous selection.
    */
    allElements.forEach((element) => {
      element.setAttribute('aria-selected', 'false');
    });

    activeElement.setAttribute('aria-selected', 'true');

    /*
      Your custom rows do not necessarily have IDs.
      Only set aria-activedescendant when an ID exists.
    */
    if (activeElement.id) {
      this.input.setAttribute(
        'aria-activedescendant',
        activeElement.id
      );
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }

    this.setLiveRegionText(
      activeElement.textContent.trim()
    );
  }


  /* ==============================
     SELECT KEYBOARD OPTION
  ============================== */

  selectOption() {
    const selected = this.querySelector(
      '[role="option"][aria-selected="true"] a, ' +
      '[role="option"][aria-selected="true"] button'
    );

    if (selected) {
      selected.click();
    }
  }


  /* ==============================
     CANCEL SEARCH
  ============================== */

  cancelSearch() {
    clearTimeout(this.searchTimer);

    this.searchTimer = null;

    if (this.searchController) {
      this.searchController.abort();
      this.searchController = null;
    }

    /*
      Increment request ID so an old response
      can never update the UI.
    */
    this.searchRequestId++;
  }


  /* ==============================
     CACHE
  ============================== */

  getCachedResults(queryKey) {
    if (!this.cachedResults.has(queryKey)) {
      return null;
    }

    /*
      Refresh cache position.
      This gives us simple LRU-style behavior.
    */
    const value = this.cachedResults.get(queryKey);

    this.cachedResults.delete(queryKey);
    this.cachedResults.set(queryKey, value);

    return value;
  }


  setCachedResults(queryKey, resultsMarkup) {
    /*
      Remove existing entry first so it moves
      to the newest position.
    */
    this.cachedResults.delete(queryKey);

    this.cachedResults.set(
      queryKey,
      resultsMarkup
    );

    /*
      Keep memory usage tiny.
    */
    while (
      this.cachedResults.size >
      this.maxCacheSize
    ) {
      const oldestKey =
        this.cachedResults.keys().next().value;

      this.cachedResults.delete(oldestKey);
    }
  }


  /* ==============================
     GET SEARCH RESULTS
  ============================== */

  getSearchResults(searchTerm) {
    const queryKey = searchTerm
      .trim()
      .toLowerCase();

    if (!queryKey) return;

    /*
      Don't show loading state if we already have
      cached results.
    */
    const cachedResults =
      this.getCachedResults(queryKey);

    if (cachedResults) {
      this.renderSearchResults(cachedResults);
      this.updateViewAllLink(searchTerm);
      return;
    }

    this.setLiveRegionLoadingState();

    /*
      Cancel previous network request.
    */
    if (this.searchController) {
      this.searchController.abort();
    }

    this.searchController =
      new AbortController();

    const requestId =
      ++this.searchRequestId;

    /*
      Shopify Predictive Search request.

      Only ONE network request is made.

      No extra /search request.
      No getTotalResults().
    */
    const params = new URLSearchParams();

    params.set('q', searchTerm);
    params.set(
      'resources[type]',
      'product,collection'
    );
    params.set(
      'resources[limit]',
      '10'
    );
    params.set(
      'resources[limit_scope]',
      'each'
    );
    params.set(
      'section_id',
      'predictive-search'
    );

    const url =
      `${routes.predictive_search_url}?${params.toString()}`;

    fetch(url, {
      signal: this.searchController.signal,

      /*
        Keep request priority normal.
        Search is user-triggered, so browser should
        prioritize it appropriately.
      */
      priority: 'high'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Predictive search failed: ${response.status}`
          );
        }

        return response.text();
      })

      .then((text) => {
        /*
          Ignore stale response.

          Example:

          "yel" request
          "yellow" request

          If "yel" finishes AFTER "yellow",
          it cannot overwrite yellow results.
        */
        if (
          requestId !== this.searchRequestId
        ) {
          return;
        }

        /*
          Make sure the user hasn't changed
          the query while request was running.
        */
        if (
          this.getQuery()
            .toLowerCase() !== queryKey
        ) {
          return;
        }

        /*
          Parse returned Shopify section.
        */
        const parser =
          new DOMParser();

        const documentFragment =
          parser.parseFromString(
            text,
            'text/html'
          );

        const section =
          documentFragment.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!section) {
          throw new Error(
            'Predictive search section not found'
          );
        }

        const resultsMarkup =
          section.innerHTML;

        /*
          Store result in small cache.
        */
        this.setCachedResults(
          queryKey,
          resultsMarkup
        );

        /*
          Render only the latest response.
        */
        this.renderSearchResults(
          resultsMarkup
        );

        this.updateViewAllLink(
          searchTerm
        );
      })

      .catch((error) => {
        /*
          AbortController cancellation is expected.
          Don't log it as an error.
        */
        if (
          error.name === 'AbortError'
        ) {
          return;
        }

        /*
          Ignore stale requests.
        */
        if (
          requestId !== this.searchRequestId
        ) {
          return;
        }

        console.error(
          'Predictive search error:',
          error
        );

        /*
          IMPORTANT:
          Do NOT close the search.

          A failed request should not make
          the search UI disappear.
        */
        this.removeAttribute(
          'loading'
        );
      });
  }


  /* ==============================
     LIVE REGION
  ============================== */

  setLiveRegionLoadingState() {
    if (!this.statusElement) {
      this.statusElement =
        this.querySelector(
          '.predictive-search-status'
        );
    }

    if (!this.loadingText) {
      this.loadingText =
        this.getAttribute(
          'data-loading-text'
        ) || 'Searching...';
    }

    this.setLiveRegionText(
      this.loadingText
    );

    this.setAttribute(
      'loading',
      'true'
    );
  }


  setLiveRegionText(statusText) {
    /*
      IMPORTANT:
      Your custom Liquid does not necessarily
      contain the live-region element.

      Never allow that to crash search.
    */
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText || '';

    clearTimeout(
      this.liveRegionTimer
    );

    this.liveRegionTimer =
      setTimeout(() => {
        if (this.statusElement) {
          this.statusElement.setAttribute(
            'aria-hidden',
            'true'
          );
        }
      }, 1000);
  }


  /* ==============================
     RENDER RESULTS
  ============================== */

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      return;
    }

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      'true'
    );

    this.setAttribute(
      'open',
      'true'
    );

    this.setLiveRegionResults();

    this.open();

    this.hideTrendingAndProducts();
  }


  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    if (countElement) {
      this.setLiveRegionText(
        countElement.textContent
      );
    }
  }


  /* ==============================
     MAX HEIGHT
  ============================== */

  getResultsMaxHeight() {
    const header =
      document.querySelector(
        '[class^="header-navigation"]'
      );

    if (!header) {
      return window.innerHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      header.getBoundingClientRect().bottom;

    return this.resultsMaxHeight;
  }


  /* ==============================
     OPEN
  ============================== */

  open() {
    this.setAttribute(
      'open',
      'true'
    );

    if (this.input) {
      this.input.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    this.isOpen = true;
  }


  /* ==============================
     CLOSE
  ============================== */

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.cancelSearch();

      if (this.input) {
        this.input.value = '';
      }

      this.removeAttribute(
        'results'
      );
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        'false'
      );
    }

    if (this.input) {
      this.input.removeAttribute(
        'aria-activedescendant'
      );

      this.input.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    this.removeAttribute(
      'open'
    );

    this.removeAttribute(
      'loading'
    );

    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults
        .removeAttribute('style');
    }

    this.isOpen = false;
  }


  /* ==============================
     TRENDING
  ============================== */

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) {
      return;
    }

    this.trendingAndProductsBlock
      .classList.add('is-show');

    this.trendingAndProductsBlock
      .classList.remove('hidden');
  }


  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) {
      return;
    }

    this.trendingAndProductsBlock
      .classList.remove('is-show');

    this.trendingAndProductsBlock
      .classList.add('hidden');
  }


  /* ==============================
     DOCUMENT CLICK
  ============================== */

  onDocClick(event) {
    const target = event.target;

    if (!target) return;

    const isInside =
      this.contains(target);

    const clickedCloseButton =
      target.closest(
        '.header-search-popup-close, ' +
        '.header-search-close'
      );

    if (
      !isInside ||
      clickedCloseButton
    ) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }


  /* ==============================
     STICKY SEARCH CLOSE
  ============================== */

  onCloseStickySearchClick() {
    this.close();

    this.hideTrendingAndProducts();

    const body = document.body;

    if (body) {
      body.classList.remove(
        'sticky-search-open'
      );

      body.classList.remove(
        'sticky-search-menu-open'
      );
    }

    const header =
      this.closest(
        '[class*="section-header-"]'
      );

    if (header) {
      header.classList.remove(
        'sticky-search-menu-open'
      );

      header.classList.remove(
        'sticky-search-menu-custom-open'
      );
    }
  }


  /* ==============================
     VIEW ALL LINK
  ============================== */

  updateViewAllLink(searchTerm) {
    const qsViewAllLink =
      this.querySelector(
        '[data-qs-view-all-link]'
      );

    if (!qsViewAllLink) {
      return;
    }

    qsViewAllLink.href =
      `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product`;
  }


  /* ==============================
     TRENDING CLICK
  ============================== */

  quickSearchTrendingClick(event) {
    const href =
      event.currentTarget.getAttribute(
        'href'
      );

    if (href) {
      window.location.href = href;
    }
  }


  /* ==============================
     CLEANUP
  ============================== */

  disconnectedCallback() {
    clearTimeout(
      this.searchTimer
    );

    clearTimeout(
      this.liveRegionTimer
    );

    if (this.searchController) {
      this.searchController.abort();
    }
  }
}


/* ==============================
   PREVENT DOUBLE REGISTRATION
============================== */

if (
  !customElements.get('predictive-search')
) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}